<script setup lang="ts">
/**
 * Billing index page (T05).
 *
 * Shows the current GNK balance, a recent-top-ups table sourced from
 * `/api/ledger?type=topup`, and a placeholder for downloadable invoices.
 * The "Top Up" button routes to the dedicated /billing/topup subpage
 * (existing TopupView).
 *
 * We intentionally call `ledgerApi.list({ types: ['topup'] })` directly
 * instead of using `useLedgerStore.setTypes(...)`, so this view does
 * not mutate the shared ledger filter state used by /ledger.
 */
import { onMounted, ref } from 'vue';
import { useBalanceStore } from '../stores/balance';
import { ledgerApi, type LedgerEntry } from '../api/ledger';
import {
  formatNgonka,
  ngonkaToGnk,
  formatUsd,
  formatDateTime,
  shortId,
} from '../lib/format';

const balance = useBalanceStore();

const topups = ref<LedgerEntry[]>([]);
const topupsLoading = ref(false);
const topupsError = ref<string | null>(null);

async function loadTopups(): Promise<void> {
  topupsLoading.value = true;
  topupsError.value = null;
  try {
    const res = await ledgerApi.list({ types: ['topup'], limit: 20 });
    topups.value = res.entries;
  } catch (err) {
    topupsError.value = (err as Error).message;
  } finally {
    topupsLoading.value = false;
  }
}

onMounted(async () => {
  await Promise.all([balance.load(), loadTopups()]);
});
</script>

<template>
  <div class="max-w-4xl mx-auto px-6 py-8 flex flex-col gap-6">
    <!-- Header -->
    <header class="flex items-center justify-between gap-3 flex-wrap">
      <h1 class="text-xl font-semibold">Billing</h1>
      <router-link to="/billing/topup" class="btn-primary text-sm">
        Top Up
      </router-link>
    </header>

    <!-- Current Balance -->
    <section class="card p-6">
      <h2 class="text-sm uppercase tracking-wide text-muted mb-4">
        Current Balance
      </h2>
      <div
        v-if="balance.loading && !balance.data"
        class="text-muted text-sm"
      >
        Loading…
      </div>
      <div v-else-if="balance.error" class="text-danger text-sm">
        {{ balance.error }}
      </div>
      <div
        v-else-if="balance.data"
        class="flex items-baseline gap-6 flex-wrap"
      >
        <div>
          <div class="text-3xl font-semibold">
            {{ ngonkaToGnk(balance.data.balance_ngonka) }}
            <span class="text-base text-muted ml-1">GNK</span>
          </div>
          <div class="text-xs text-muted mt-1">
            {{ formatNgonka(balance.data.balance_ngonka) }} ngonka
          </div>
        </div>
        <div class="text-muted text-sm">
          ≈ {{ formatUsd(balance.data.balance_usd) }}
          <span v-if="balance.data.gnk_usd_rate > 0" class="ml-2">
            @ ${{ balance.data.gnk_usd_rate.toFixed(4) }} / GNK
          </span>
        </div>
      </div>
    </section>

    <!-- Recent top-ups -->
    <section class="card p-6">
      <div class="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 class="text-sm uppercase tracking-wide text-muted">
          Recent top-ups
        </h2>
        <button
          class="btn-ghost text-xs"
          :disabled="topupsLoading"
          @click="loadTopups"
        >
          {{ topupsLoading && topups.length > 0 ? 'Refreshing…' : 'Refresh' }}
        </button>
      </div>

      <div v-if="topupsError" class="text-danger text-sm mb-3">
        {{ topupsError }}
      </div>

      <div
        v-if="topupsLoading && topups.length === 0"
        class="text-muted text-sm"
      >
        Loading…
      </div>
      <div
        v-else-if="topups.length === 0"
        class="text-muted text-sm italic py-2"
      >
        No top-ups yet.
        <router-link
          to="/billing/topup"
          class="text-accent hover:underline ml-1"
        >Top up your balance →</router-link>
      </div>
      <div v-else class="overflow-x-auto">
        <table class="w-full table">
          <thead>
            <tr>
              <th>When</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Payment method</th>
              <th>Transaction</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="e in topups" :key="e.id">
              <td class="text-muted whitespace-nowrap">
                {{ formatDateTime(e.created_at) }}
              </td>
              <td class="text-ok">
                {{ formatNgonka(e.amount_ngonka) }}
                <span class="text-muted ml-1">ngonka</span>
              </td>
              <td>
                <span class="pill-on">✓ confirmed</span>
              </td>
              <td class="text-muted">{{ e.topup_source ?? 'on-chain' }}</td>
              <td>
                <code v-if="e.req_id">{{ shortId(e.req_id, 6, 6) }}</code>
                <span v-else class="text-muted">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Invoices / receipts (coming soon) -->
    <section class="card p-6">
      <h2 class="text-sm uppercase tracking-wide text-muted mb-2">
        Invoices / receipts
      </h2>
      <p class="text-muted text-sm">
        Coming soon — downloadable PDF receipts for each top-up.
      </p>
    </section>
  </div>
</template>
